<template>
  <page-template :is-loading="isLoading" :data-exists="!!mobileApplicationGroups">
    <template #pageHeadline>
      {{ $t('mobileApplicationGroup.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="mobileApplicationGroups"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="mobile-application-groups-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewMobileApplicationGroup()"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateMobileApplicationGroup"
        @delete-row-button-clicked="openDeleteMobileApplicationGroupModal"
        @download-data="downloadDoc"
      >
      </rbz-table>
      <rbz-modal-dialog
        :show="showDeleteMobileApplicationGroupModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteMobileApplicationGroup"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteMobileApplicationGroupModal = false"
        @closeModal="showDeleteMobileApplicationGroupModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', {
              name: selectedMobileApplicationGroup.name,
            })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { DocumentName, MobileApplicationGroup, ServerGroup } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'MobileApplicationGroupsList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('mobileApplicationGroup.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getMobileApplicationGroupPath(item);
          },
        },
        {
          id: 'grace-period',
          title: this.$t('mobileApplicationGroup.listPage.gracePeriod'),
          fieldNames: ['grace'],
          isSortable: true,
          isSearchable: true,
          isNumber: true,
          classes: 'width-120px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'token-header-name',
          title: this.$t('mobileApplicationGroup.listPage.tokenHeaderName'),
          fieldNames: ['uid_header'],
          isSortable: true,
          isSearchable: true,
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getMobileApplicationGroupPath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      mobileApplicationGroups: [],
      pageLoadingCounter: null,

      showDeleteMobileApplicationGroupModal: false,
      selectedMobileApplicationGroup: null as MobileApplicationGroup,

      referencedIds: [],
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if (
          (this.$route.name as string).includes('Mobile Application Groups List') &&
          val &&
          val !== oldVal
        ) {
          this.setPageLoadingStatus(true);
          await this.loadMobileApplicationGroups();
          await this.loadReferencedIds();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('mobileApplicationGroup.plural'),
        singular: this.$t('mobileApplicationGroup.singular'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    getNewMobileApplicationGroup() {
      return this.getNewPath('mobile-application-groups');
    },

    getMobileApplicationGroupPath(mobileApplicationGroup: MobileApplicationGroup) {
      return this.getEditPath('mobile-application-groups', mobileApplicationGroup.id);
    },

    editMobileApplicationGroup(mobileApplicationGroup: MobileApplicationGroup) {
      this.navigateTo(this.getMobileApplicationGroupPath(mobileApplicationGroup));
    },

    async duplicateMobileApplicationGroup(mobileApplicationGroup: MobileApplicationGroup) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/mobile-application-groups/${mobileApplicationGroup.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalMobileApplicationGroup = getOriginalResponse.data;
      const mobileApplicationGroupToAdd = _.cloneDeep(originalMobileApplicationGroup);
      mobileApplicationGroupToAdd.id = DatasetsUtils.generateUUID2();
      mobileApplicationGroupToAdd.name = Utils.generateUniqueEntityName(
        mobileApplicationGroupToAdd.name,
        _.map(this.mobileApplicationGroups, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/mobile-application-groups/${mobileApplicationGroupToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: mobileApplicationGroupToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editMobileApplicationGroup(mobileApplicationGroupToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteMobileApplicationGroup() {
      this.setPageLoadingStatus(true);
      const mobileApplicationGroupId = this.selectedMobileApplicationGroup.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/mobile-application-groups/${mobileApplicationGroupId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.mobileApplicationGroups = _.filter(
          this.mobileApplicationGroups,
          (mobileApplicationGroup) => {
            return mobileApplicationGroup.id !== mobileApplicationGroupId;
          }
        );
      }
      this.showDeleteMobileApplicationGroupModal = false;
      this.setPageLoadingStatus(false);
    },

    downloadDoc() {
      Utils.downloadFile('mobile-application-groups', 'json', this.mobileApplicationGroups);
    },

    openDeleteMobileApplicationGroupModal(mobileApplicationGroup: MobileApplicationGroup) {
      this.showDeleteMobileApplicationGroupModal = true;
      this.selectedMobileApplicationGroup = mobileApplicationGroup;
    },

    async loadMobileApplicationGroups() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/mobile-application-groups`;
      const fieldNames = _.flatMap(this.columns, 'fieldNames');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
      });
      this.mobileApplicationGroups = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/server-groups`,
      });
      const serverGroups = response?.data?.items || [];
      const referencedIds: string[] = [];
      _.forEach(serverGroups, (serverGroup: ServerGroup) => {
        referencedIds.push(serverGroup['mobile_application_group']);
      });
      this.referencedIds = _.uniq(referencedIds);
      this.setPageLoadingStatus(false);
    },

    isDeleteDisabled(mobileApplicationGroup: MobileApplicationGroup) {
      return this.isMobileApplicationGroupReferenced(mobileApplicationGroup);
    },

    isMobileApplicationGroupReferenced(mobileApplicationGroup: MobileApplicationGroup): boolean {
      return this.referencedIds.includes(mobileApplicationGroup.id);
    },

    deleteTooltip(mobileApplicationGroup: MobileApplicationGroup): string {
      if (this.isMobileApplicationGroupReferenced(mobileApplicationGroup)) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },
  },
});
</script>
